import { publicEnv } from '@/shared/config/publicEnv'
import * as amplitude from '@amplitude/analytics-browser'
import type { EventName, EventProperties } from '@/shared/models/analyticsModels'
import { v4 as uuidv4 } from 'uuid'
import { isString } from 'lodash'

const ANON_ID_KEY = 'amplitude_anon_id'

const getOrCreateAnonId = (): string => {
    if (typeof window === 'undefined') return ''

    let anonId = localStorage.getItem(ANON_ID_KEY)
    if (!anonId) {
        anonId = uuidv4()
        localStorage.setItem(ANON_ID_KEY, anonId)
    }
    return anonId
}

const getIsProd = () => {
    const key = publicEnv.amplitudeApiKey
    const isProd = publicEnv.environment === 'production' && isString(key)
    return isProd
}

let isInitialized = false

export const initAnalytics = () => {
    const isProd = getIsProd()

    if (isInitialized || !isProd) {
        if (!isProd) {
            console.info('[Amplitude] Skipped initialization (non-production environment)')
        }
        return
    }

    // eslint-disable-next-line typescript/no-unsafe-type-assertion -- every deployment sets this variable, and the call has always read it as set
    amplitude.init(publicEnv.amplitudeApiKey as string, {
        defaultTracking: {
            pageViews: false,
            sessions: true
        }
    })

    setUserContext(getOrCreateAnonId())

    isInitialized = true
}

export const setUserContext = (userId: string) => {
    const isProd = getIsProd()

    if (!isProd) {
        console.info('[Amplitude] setUserContext', { userId })
        return
    }

    if (!userId) {
        const anonId = getOrCreateAnonId()
        amplitude.setUserId(anonId)
        return
    }

    amplitude.setUserId(userId)
}

export const clearUserContext = () => {
    const isProd = getIsProd()

    if (!isProd) {
        console.info('[Amplitude] clearUserContext')
        return
    }

    amplitude.reset()
}

export function trackEvent<T extends EventName>(eventName: T, properties?: EventProperties<T>): void {
    const isProd = getIsProd()

    if (!isProd) {
        console.info('[Amplitude] trackEvent', eventName, properties)
        return
    }

    amplitude.track(eventName, properties)
}
